import { lazy, Suspense } from 'react';
import { Navigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { useQuery } from '@tanstack/react-query';
import { agentQueryOptions } from '@/entities/agent';
import { ROUTES } from '@/shared/config/routes';
// Deep imports on purpose: the slice's index also carries the canvas, which only the lazy route loads.
import { isAgentDefinitionAgentId } from '@/features/agent-builder/model/types';
import { AgentBuilderSkeleton } from '@/features/agent-builder/ui/AgentBuilderSkeleton';
import type { BuilderChats } from '@/features/agent-builder/model/builder-chats';
import { AdminPageShell } from '../AdminPageShell';

const AgentBuilderRoute = lazy(() =>
  import('./AgentBuilderRoute').then((m) => ({ default: m.AgentBuilderRoute })),
);

function Builder({ chats }: { chats: BuilderChats }) {
  // Suspends until flags have loaded, so the page never flashes the Tools list before the builder.
  const builderOn = useBooleanFlagValue('relay_agent_builder', false, { suspendUntilReady: true });
  const { agentId } = useParams<{ agentId?: string }>();

  if (!builderOn) return <Navigate to={ROUTES.ADMIN_TOOLS} replace />;
  if (agentId && !isAgentDefinitionAgentId(agentId)) {
    return <LegacyAgentRedirect agentId={agentId} />;
  }
  return <AgentBuilderRoute {...(agentId ? { definitionId: agentId } : {})} chats={chats} />;
}

/**
 * An old link to a legacy agent's editor. The API answers a legacy id with the definition it was
 * imported into, whose editor this opens; an agent not imported, or not readable, has no editor
 * any more, so the Tools list stands in.
 */
function LegacyAgentRedirect({ agentId }: { agentId: string }) {
  const { data, isPending } = useQuery({ ...agentQueryOptions(agentId), retry: false });
  if (isPending) return <LoadingBuilder />;
  const definitionId = data?.id;
  return (
    <Navigate
      to={
        definitionId && isAgentDefinitionAgentId(definitionId)
          ? ROUTES.ADMIN_EDIT_AGENT(definitionId)
          : ROUTES.ADMIN_TOOLS
      }
      replace
    />
  );
}

/** The page as it will be while its code and flags load: its header, and the builder's shape. */
function LoadingBuilder() {
  const { t } = useTranslation('agentBuilder');
  return (
    <AdminPageShell
      title=""
      titlePending
      parents={[{ label: t('page.tools'), href: ROUTES.ADMIN_TOOLS }]}
      cardless
    >
      <AgentBuilderSkeleton />
    </AdminPageShell>
  );
}

/** `/admin/tools/new` and `/admin/tools/:agentId/edit`: the agent builder. */
export function AgentBuilderPage({ chats }: { chats: BuilderChats }) {
  return (
    <Suspense fallback={<LoadingBuilder />}>
      <Builder chats={chats} />
    </Suspense>
  );
}
